import React, { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HotelIcon from '@mui/icons-material/HotelOutlined';
import TourIcon from '@mui/icons-material/Tour';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { useDeleteBundleComponent } from '~/hooks/apis/bundle/useBundles';
import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOfferPackages } from '~/hooks/apis/offers/useGetOfferPackages';
import { useGetTourOfferSummary } from '~/hooks/apis/tours/useTourOffers';

import { Bundle, BundleComponent, HotelBundleComponent, TourBundleComponent } from '~/types/bundles/bundle';

import HotelComponentDialog from './HotelComponentDialog';
import ItineraryTimeline, { TimelineSegment } from './ItineraryTimeline';
import TourComponentDialog from './TourComponentDialog';
import TourOfferName from './TourOfferName';
import { hotelPackageOptions } from './hotelPackages';
import useBundleTourSpan from './useBundleTourSpan';

interface Props {
  bundle: Bundle;
}

function pluralise(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

// The bundle stores days from 0; people count the departure day as day 1.
const dayRange = (startDay: number, days: number) => `Days ${startDay + 1}–${startDay + days}`;

interface RowProps {
  icon: React.ReactNode;
  color: 'primary' | 'secondary';
  title: React.ReactNode;
  subtitle: React.ReactNode;
  days: string;
  onRemove?: () => void;
  removeLabel: string;
}

function ItineraryRow({ icon, color, title, subtitle, days, onRemove, removeLabel }: RowProps) {
  return (
    <Stack direction="row" spacing={2} alignItems="center" px={2} py={1.5}>
      <Avatar sx={{ bgcolor: `${color}.main`, width: 36, height: 36 }}>{icon}</Avatar>
      <Box flex={1} minWidth={0}>
        <Typography variant="subtitle1" noWrap>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {subtitle}
        </Typography>
      </Box>
      <Chip size="small" variant="outlined" label={days} />
      {onRemove && (
        <Tooltip title={removeLabel}>
          <IconButton aria-label={removeLabel} onClick={onRemove}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function TourRow({ tour, days, onRemove }: { tour: TourBundleComponent; days: string; onRemove?: () => void }) {
  const { data: offer } = useGetTourOfferSummary(tour.tour_offer_id);
  return (
    <ItineraryRow
      icon={<TourIcon fontSize="small" />}
      color="primary"
      title={<TourOfferName tourOfferId={tour.tour_offer_id} />}
      subtitle={offer?.isLtle ? 'LTLE · made from the base tour' : 'Base tour · its LTLE is made on approval'}
      days={days}
      onRemove={onRemove}
      removeLabel="Remove tour"
    />
  );
}

function HotelRow({ hotel, onRemove }: { hotel: HotelBundleComponent; onRemove?: () => void }) {
  const { data: offer, isLoading } = useGetOffer(hotel.offer_id);
  const { data: packages = [] } = useGetOfferPackages(hotel.offer_id);
  const packageName = useMemo(
    () => hotelPackageOptions(packages).find((option) => option.packageId === hotel.package_id)?.name,
    [packages, hotel.package_id],
  );
  const checkInDay = hotel.day_offset ?? 0;
  return (
    <ItineraryRow
      icon={<HotelIcon fontSize="small" />}
      color="secondary"
      title={isLoading ? <Skeleton width={220} /> : (offer?.name ?? hotel.offer_id)}
      subtitle={`Flash hotel · ${packageName ?? hotel.package_id}`}
      days={`${dayRange(checkInDay, hotel.nights + 1)} · ${pluralise(hotel.nights, 'night')}`}
      onRemove={onRemove}
      removeLabel="Remove hotel"
    />
  );
}

export default function BundleItinerary({ bundle }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isTourDialogOpen, setIsTourDialogOpen] = useState(false);
  const [isHotelDialogOpen, setIsHotelDialogOpen] = useState(false);
  const [toRemove, setToRemove] = useState<BundleComponent | null>(null);

  const { tour, startDay, tourDays } = useBundleTourSpan(bundle);
  const deleteComponent = useDeleteBundleComponent(bundle.id);

  const hotels = bundle.components.filter(
    (component): component is HotelBundleComponent => component.product_type === 'flashAccommodation',
  );
  const isDraft = bundle.status === 'draft';

  const segments: Array<TimelineSegment> = [
    ...(tour && tourDays !== undefined
      ? [{ key: tour.id, label: 'Tour', startDay, endDay: startDay + tourDays - 1, color: 'primary' as const }]
      : []),
    ...hotels.map((hotel) => ({
      key: hotel.id,
      label: `Hotel · ${pluralise(hotel.nights, 'night')}`,
      startDay: hotel.day_offset ?? 0,
      endDay: (hotel.day_offset ?? 0) + hotel.nights,
      color: 'secondary' as const,
    })),
  ];

  const handleRemove = async () => {
    try {
      await deleteComponent.mutateAsync(toRemove.id);
      enqueueSnackbar(toRemove.product_type === 'tour' ? 'Tour removed' : 'Hotel removed', { variant: 'success' });
      setToRemove(null);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  if (!tour) {
    return (
      <>
        <Paper variant="outlined" sx={{ borderStyle: 'dashed', p: 6, textAlign: 'center' }}>
          <Stack spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: 'primary.main', width: 52, height: 52 }}>
              <TourIcon />
            </Avatar>
            <Box>
              <Typography variant="h6">Start with the base tour</Typography>
              <Typography color="text.secondary">
                Pick the tour this bundle's LTLE is made from and the departures to sell, then pair it with a flash
                hotel.
              </Typography>
            </Box>
            {isDraft && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsTourDialogOpen(true)}>
                Add tour
              </Button>
            )}
          </Stack>
        </Paper>
        <TourComponentDialog bundle={bundle} open={isTourDialogOpen} onClose={() => setIsTourDialogOpen(false)} />
      </>
    );
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={1.5}>
            <Typography variant="h6">Itinerary</Typography>
            {segments.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {pluralise(Math.max(...segments.map((segment) => segment.endDay)) + 1, 'day')}
              </Typography>
            )}
          </Stack>
          {segments.length > 0 && <ItineraryTimeline segments={segments} />}
        </CardContent>

        <Divider />

        <TourRow
          tour={tour}
          days={tourDays === undefined ? `From day ${startDay + 1}` : dayRange(startDay, tourDays)}
          onRemove={isDraft ? () => setToRemove(tour) : undefined}
        />
        {hotels.map((hotel) => (
          <React.Fragment key={hotel.id}>
            <Divider />
            <HotelRow hotel={hotel} onRemove={isDraft ? () => setToRemove(hotel) : undefined} />
          </React.Fragment>
        ))}

        {/* A bundle is one tour and one hotel for now. */}
        {isDraft && hotels.length === 0 && (
          <>
            <Divider />
            <ButtonBase
              onClick={() => setIsHotelDialogOpen(true)}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                px: 2,
                py: 1.5,
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{ bgcolor: 'action.selected', color: 'text.secondary', width: 36, height: 36 }}>
                  <AddIcon fontSize="small" />
                </Avatar>
                <Box textAlign="left">
                  <Typography variant="subtitle1" color="primary">
                    Add a flash hotel
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Placed on the bundle's days, checked against every departure
                  </Typography>
                </Box>
              </Stack>
            </ButtonBase>
          </>
        )}
      </Card>

      <HotelComponentDialog bundle={bundle} open={isHotelDialogOpen} onClose={() => setIsHotelDialogOpen(false)} />

      <Dialog open={!!toRemove} onClose={() => setToRemove(null)}>
        <DialogTitle>{toRemove?.product_type === 'tour' ? 'Remove tour?' : 'Remove hotel?'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {toRemove?.product_type === 'tour'
              ? 'Its departures, prices and price adjustments are removed with it.'
              : 'Its prices and price adjustments are removed with it.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToRemove(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRemove} disabled={deleteComponent.isPending}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
